import React, { memo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import buildURL from '../../utils/buildURL'
import { applicationStateShape } from '../../utils/shapes'
import replaceApplicationStatePlaceholders from '../../utils/replaceApplicationStatePlaceholders'
import verifyParameters from '../../utils/verifyParameters'
// eslint-disable-next-line import/no-named-as-default
import Helmet from 'react-helmet'

const TRACKING_PIXEL_DATA_TESTID = 'tracking-pixel'

function TrackingPixel({ applicationState, events, type, url }) {
  const [shouldRenderPixel, setShouldRenderPixel] = useState(false)
  const { pageType } = applicationState

  useEffect(() => {
    // only return a component if window and all required props exists
    if (typeof window === 'object' && typeof applicationState === 'object' && typeof events === 'object') {
      setShouldRenderPixel(true)
    }
  }, [applicationState, events])

  const eventTypes = {
    init: true,
    pageview: true,
    landing: pageType === 'landing' || pageType === 'landing_flights',
    index: pageType === 'index',
    search:
      pageType === 'search_results' ||
      pageType === 'search_results_hotels' ||
      pageType === 'search_results_flights' ||
      pageType === 'search_results_cars',
    details: pageType === 'product_details',
    checkout: pageType === 'checkout_process',
    success: pageType === 'post_booking',
    promotion: pageType === 'promotion',
    others: pageType === 'others',
  }
  const src = {}
  let parameters

  // handle events
  Object.keys(events).forEach((event) => {
    // make sure the event type is valid
    if (eventTypes[event]) {
      const eventUrl = url || events[event].url
      parameters = replaceApplicationStatePlaceholders(events[event].parameters, applicationState)
      // verify that we have values for all parameters
      if (eventUrl && verifyParameters(parameters)) {
        src[event] = buildURL(replaceApplicationStatePlaceholders(eventUrl, applicationState), parameters)
      }
    }
  })

  return shouldRenderPixel && Object.keys(src).length > 0
    ? Object.keys(src).map((key) => {
        switch (type) {
          case 'image':
            return (
              <img
                data-testid={TRACKING_PIXEL_DATA_TESTID}
                alt=''
                key={key}
                src={src[key]}
                style={{ position: 'absolute' }}
              />
            )
          case 'iframe':
            return (
              <iframe
                data-testid={TRACKING_PIXEL_DATA_TESTID}
                frameBorder='0'
                height='1'
                key={key}
                src={src[key]}
                style={{ display: 'none' }}
                title={key}
                width='1'
              />
            )
          case 'script':
            return (
              <Helmet>
                <script data-testid={TRACKING_PIXEL_DATA_TESTID} key={key} src={src[key]} />
              </Helmet>
            )
          default:
            return null
        }
      })
    : null
}

TrackingPixel.propTypes = {
  type: PropTypes.oneOf(['image', 'iframe', 'script']).isRequired,
  // This url will be loaded into an image tag for each event.
  url: PropTypes.string,
  // An object of events specifying when to load the pixel and what data to pass.
  events: PropTypes.object,
  // An object of application data that can be used for events.
  applicationState: applicationStateShape,
}

export default memo(TrackingPixel)
